import { concatHex, keccak256, toHex } from "viem";
import type { Address, Hex, TypedDataDomain } from "viem";
import { privateKeyToAccount, type PrivateKeyAccount } from "viem/accounts";

/**
 * The identities a browser derives for a wallet (D73): the Console's own identity and one identity per installed
 * extension. The wallet signs the root once per browser: EIP-712 typed data of its own (the domain "Woodpecker
 * identity", the type IdentityRoot), a deterministic signature of an EOA; the root is keccak256 of that signature and
 * stays in the browser (D50a); every identity is the key keccak256 of the root and its name. The same wallet derives
 * the same identities on every browser, so a restore brings them back without any record of their keys. They sign
 * Intents and sign-in challenges like a conversation's agent; a service accepts one that contract A shows authorized
 * by the wallet (D47a). The root is typed data and never a personal_sign text, so no service can ask for the root
 * signature through a sign-in challenge (0.2): a challenge is signed only as the Console rebuilds it from its fields.
 */
export const IDENTITY_ROOT_VERSION = 1;

export const IDENTITY_ROOT_TYPES = {
  IdentityRoot: [
    { name: "wallet", type: "address" },
    { name: "version", type: "uint8" },
  ],
} as const;

export function identityRootDomain(chainId: number): TypedDataDomain {
  return { name: "Woodpecker identity", version: "1", chainId };
}

/** A signer of the root: a viem account (the wallet key in the browser). */
export interface RootSigner {
  address: Address;
  signTypedData(args: {
    domain: TypedDataDomain;
    types: typeof IDENTITY_ROOT_TYPES;
    primaryType: "IdentityRoot";
    message: { wallet: Address; version: number };
  }): Promise<Hex>;
}

/** The wallet signs the root's typed data (once per browser) and the root is keccak256 of the signature. */
export async function signIdentityRoot(wallet: RootSigner, chainId: number): Promise<Hex> {
  const signature = await wallet.signTypedData({
    domain: identityRootDomain(chainId),
    types: IDENTITY_ROOT_TYPES,
    primaryType: "IdentityRoot",
    message: { wallet: wallet.address, version: IDENTITY_ROOT_VERSION },
  });
  return identityRoot(signature);
}

/** The root: keccak256 of the wallet's signature over the root's typed data. */
export function identityRoot(signature: Hex): Hex {
  return keccak256(signature);
}

/** The name of the Console's own identity under the root. */
export const CONSOLE_IDENTITY = "console";

/** The name of an installed extension's identity under the root: its id, `handle/name`. */
export function extensionIdentityName(extensionId: string): string {
  return `extension:${extensionId}`;
}

/** The private key of one identity: keccak256 of the root and the identity's name. */
export function identityKey(root: Hex, name: string): Hex {
  return keccak256(concatHex([root, toHex(name)]));
}

/** The identity as a viem account: it signs Intents (EIP-712) and sign-in challenges (personal_sign). */
export function identityAccount(root: Hex, name: string): PrivateKeyAccount {
  return privateKeyToAccount(identityKey(root, name));
}
